<!--
WorkspaceDashboard

Primary dashboard shell that chooses the owner, team-leader, team-member,
or no-access layout from the backend summary. Owner users continue to see
commercial reporting, while operational users see recent views and upload
entry points only when the shared permissions object allows it.

Parent:
- portal/templates/home.html

Children:
- DashboardHero
- DashboardSummaryCards
- DashboardRecentDatasets
- DashboardRecentViews
- DashboardSubscriptionPanel
- DashboardTeamPanel
- DashboardActivityFeed
- DashboardUsageAnalytics
- DashboardHelpSection

Permission rules:
- dashboardRole chooses the layout.
- summary.permissions controls action visibility.

@author Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
-->
<template>
    <div class="workspace-dashboard">
        <section v-if="dashboardRole === 'none'" class="workspace-dashboard__no-access">
            <v-card class="workspace-dashboard__no-access-card" elevation="0">
                <div class="workspace-dashboard__section-kicker">No workspace access</div>
                <h1>You haven't been assigned to a team yet.</h1>
                <p>Please contact your administrator.</p>
                <div class="workspace-dashboard__hero-actions">
                    <v-btn color="primary" href="/support/">Contact Support</v-btn>
                    <v-btn variant="outlined" href="/profile">My Profile</v-btn>
                </div>
            </v-card>
        </section>
        <template v-else>
        <dashboard-hero
            :summary="summary"
            :storage-used-label="storageUsedLabel"
            :hero-visual-style="heroVisualStyle"
        />
        <dashboard-summary-cards :usage-cards="usageCards" />

        <section class="workspace-dashboard__content-grid">
            <div class="workspace-dashboard__main-column">
                <dashboard-recent-datasets
                    v-if="dashboardRole === 'owner'"
                    :summary="summary"
                    :dataset-cards="datasetCards"
                />
                <dashboard-recent-views
                    v-else
                    :summary="summary"
                    :recent-views="recentViews"
                    :recently-uploaded="recentlyUploaded"
                    :fav-views="favViews"
                />
                <div class="workspace-dashboard__ownership-note">
                    <v-icon size="16">mdi-information-outline</v-icon>
                    <span>{{ ownershipNote }}</span>
                </div>
            </div>

            <aside class="workspace-dashboard__sidebar-column">
                <dashboard-subscription-panel
                    v-if="canShowBillingPanel"
                    :summary="summary"
                />
                <dashboard-team-panel :summary="summary" />
                <dashboard-activity-feed
                    :activity-items="workspaceActivityItems"
                    :activity-url="summary.links?.activity || '/activity/'"
                />
            </aside>
        </section>

        <section v-if="canShowBillingPanel" class="workspace-dashboard__insights-grid">
            <dashboard-usage-analytics
                :analytics-highlights="analyticsHighlights"
                :storage-chart-options="storageChartOptions"
                :storage-chart-series="storageChartSeries"
                :upload-chart-options="uploadChartOptions"
                :upload-chart-series="uploadChartSeries"
            />
            <v-card class="workspace-dashboard__quick-actions-card" elevation="0">
                <div class="workspace-dashboard__section-head">
                    <div>
                        <div class="workspace-dashboard__section-kicker">Quick actions</div>
                        <h2>Workspace shortcuts</h2>
                    </div>
                </div>
                <div class="workspace-dashboard__action-grid">
                    <a
                        v-for="action in quickActions"
                        :key="action.label"
                        :href="action.href"
                        class="workspace-dashboard__action-tile"
                    >
                        <v-icon>{{ action.icon }}</v-icon>
                        <strong>{{ action.label }}</strong>
                        <span>{{ action.copy }}</span>
                    </a>
                </div>
            </v-card>
        </section>

        <dashboard-help-section :summary="summary" />
        </template>
    </div>
</template>

<script>
import DashboardActivityFeed from 'components/Dashboard/Widgets/DashboardActivityFeed.vue';
import DashboardHelpSection from 'components/Dashboard/Widgets/DashboardHelpSection.vue';
import DashboardHero from 'components/Dashboard/Widgets/DashboardHero.vue';
import DashboardNotifications from 'components/Dashboard/Widgets/DashboardNotifications.vue';
import DashboardRecentDatasets from 'components/Dashboard/Widgets/DashboardRecentDatasets.vue';
import DashboardRecentViews from 'components/Dashboard/Widgets/DashboardRecentViews.vue';
import DashboardSummaryCards from 'components/Dashboard/Widgets/DashboardSummaryCards.vue';
import DashboardSubscriptionPanel from 'components/Dashboard/Widgets/DashboardSubscriptionPanel.vue';
import DashboardTeamPanel from 'components/Dashboard/Widgets/DashboardTeamPanel.vue';
import DashboardUploadCard from 'components/Dashboard/Widgets/DashboardUploadCard.vue';
import DashboardUsageAnalytics from 'components/Dashboard/Widgets/DashboardUsageAnalytics.vue';
import {
    buildActivityItems,
    buildAnalyticsHighlights,
    buildDatasetCards,
    buildNotificationItems,
    buildUploadTrend,
    buildUsageCards,
    formatStorageFromBytes,
} from 'components/Dashboard/dashboardWorkspace';
import { mapState } from 'pinia';
import { useGeneralStore } from 'store/pinia/useGeneralStore';

function cssVar(name, fallback) {
    if (typeof window === 'undefined') {
        return fallback;
    }
    const value = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    return value || fallback;
}

export default {
    name: 'WorkspaceDashboard',
    components: {
        DashboardActivityFeed,
        DashboardHelpSection,
        DashboardHero,
        DashboardNotifications,
        DashboardRecentDatasets,
        DashboardRecentViews,
        DashboardSummaryCards,
        DashboardSubscriptionPanel,
        DashboardTeamPanel,
        DashboardUploadCard,
        DashboardUsageAnalytics,
    },
    props: {
        summary: {
            type: Object,
            default: () => ({}),
        },
        recentViews: {
            type: Array,
            default: () => [],
        },
        recentlyUploaded: {
            type: Array,
            default: () => [],
        },
        favViews: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        uploadPlatforms() {
            return ['DJI Terra', 'Agisoft', 'Pix4D', 'RealityCapture'];
        },
        featuredDatasetImage() {
            return (
                this.datasetCards.find(dataset => Boolean(dataset.image))?.image ||
                '/static/img/landing/smartdata-landing-hero.png'
            );
        },
        heroVisualStyle() {
            return {
                backgroundImage: `
                    linear-gradient(180deg, rgba(8, 18, 36, 0.08), rgba(8, 18, 36, 0.72)),
                    url("${this.featuredDatasetImage}")
                `,
            };
        },
        ...mapState(useGeneralStore, ['darkTheme']),
        dashboardRole() {
            return this.summary.dashboardRole || 'none';
        },
        permissions() {
            return this.summary.permissions || {};
        },
        canShowBillingPanel() {
            return Boolean(this.permissions.canShowBilling);
        },
        usageCards() {
            return buildUsageCards(this.summary);
        },
        datasetCards() {
            return buildDatasetCards(this.recentlyUploaded, this.recentViews);
        },
        notificationItems() {
            return buildNotificationItems(
                this.summary,
                this.recentlyUploaded,
                this.summary.teamPanel?.accessTeams || this.summary.teamPanel?.teams || []
            );
        },
        workspaceActivityItems() {
            return [
                ...this.activityItems,
                ...this.notificationItems,
            ].slice(0, 3);
        },
        analyticsHighlights() {
            return buildAnalyticsHighlights(
                this.summary,
                this.recentlyUploaded,
                this.summary.teamPanel?.accessTeams || this.summary.teamPanel?.teams || []
            );
        },
        activityItems() {
            return buildActivityItems(
                this.recentlyUploaded,
                this.summary.teamPanel?.accessTeams || this.summary.teamPanel?.teams || []
            );
        },
        quickActions() {
            const actions = [];

            if (this.permissions.canUpload && this.summary.links?.upload) {
                actions.push({
                    label: 'Upload Dataset',
                    copy: 'Start a new ingestion flow',
                    href: this.summary.links.upload,
                    icon: '$vuetify.icons.upload',
                });
            }

            actions.push(
                {
                    label: 'Open MapView',
                    copy: 'Browse locations and assets',
                    href: this.summary.links?.map || '/mapview',
                    icon: '$vuetify.icons.mapMarkerAlt',
                },
                {
                    label: 'Open Assets',
                    copy: 'Review accessible datasets',
                    href: this.summary.links?.assets || '/mapview',
                    icon: 'mdi-cube-outline',
                },
                {
                    label: 'Open Viewer',
                    copy: 'Return to recent datasets',
                    href: this.summary.links?.viewer || '/view',
                    icon: '$vuetify.icons.eye',
                }
            );

            if (this.summary.teamPanel?.inviteUrl && this.permissions.canViewTeams) {
                actions.push({
                    label: 'Manage Teams',
                    copy: 'Review team access and membership',
                    href: this.summary.teamPanel.inviteUrl,
                    icon: '$vuetify.icons.addUser',
                });
            }

            if (this.permissions.canManageBilling && this.summary.links?.billing) {
                actions.push({
                    label: 'Manage Billing',
                    copy: 'Review plan and access',
                    href: this.summary.links.billing,
                    icon: '$vuetify.icons.wallet',
                });
            }

            return actions.slice(0, 6);
        },
        storageUsedLabel() {
            return formatStorageFromBytes(this.summary.usage?.storageUsedBytes);
        },
        ownershipNote() {
            const billingAccountName =
                this.summary.workspace?.name || 'this billing account';

            if (this.dashboardRole === 'team_leader') {
                return `Operational access: ${billingAccountName} can upload and manage datasets for the teams you lead, without exposing billing or plan details.`;
            }

            if (this.dashboardRole === 'team_member') {
                return `Read-only access: ${billingAccountName} can browse locations, assets, datasets, and viewers shared with your teams, without upload or billing controls.`;
            }

            return `Ownership: This Billing Account (${billingAccountName}) owns and pays for all Dataset Groups, Datasets, and Storage. Billing Teams measure plan limits, while Access Teams listed above have access based on permissions.`;
        },
        chartThemeColors() {
            return {
                text: cssVar('--sd-text', this.darkTheme ? '#eef4ff' : '#172033'),
                muted: cssVar('--sd-text-secondary', this.darkTheme ? '#9aa7bd' : '#6f778a'),
                grid: cssVar('--sd-border', this.darkTheme ? 'rgba(255,255,255,0.12)' : '#dde5f1'),
                primary: cssVar('--sd-primary', '#338eca'),
                success: cssVar('--sd-success', '#4bc8a7'),
                warning: cssVar('--sd-warning', '#f3a55f'),
                surface: cssVar('--sd-surface', this.darkTheme ? '#182231' : '#ffffff'),
            };
        },
        storageUsagePercent() {
            const used = Number(this.summary.usage?.storageUsedBytes || 0);
            const allowed = Number(this.summary.usage?.storageAllowedBytes || 0);

            if (!allowed) {
                return 0;
            }

            return Math.min(100, Math.max(0, Math.round((used / allowed) * 100)));
        },
        storageChartSeries() {
            return [this.storageUsagePercent];
        },
        storageChartOptions() {
            return {
                chart: {
                    type: 'radialBar',
                    toolbar: { show: false },
                    fontFamily: 'Omnes Regular, Omnes, Roboto, sans-serif',
                },
                colors: [this.chartThemeColors.primary],
                dataLabels: { enabled: false },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'light',
                        type: 'vertical',
                        opacityFrom: 1,
                        opacityTo: 0.7,
                    },
                },
                plotOptions: {
                    radialBar: {
                        hollow: {
                            size: '66%',
                            background: this.chartThemeColors.surface,
                        },
                        track: {
                            background: this.chartThemeColors.grid,
                            strokeWidth: '100%',
                        },
                        dataLabels: {
                            name: {
                                color: this.chartThemeColors.muted,
                                fontSize: '12px',
                            },
                            value: {
                                color: this.chartThemeColors.text,
                                fontSize: '32px',
                                fontWeight: 700,
                                formatter: value => `${Math.round(value)}%`,
                            },
                            total: {
                                show: true,
                                label: 'Used',
                                color: this.chartThemeColors.muted,
                                formatter: () => this.storageUsedLabel,
                            },
                        },
                    },
                },
                tooltip: {
                    theme: this.darkTheme ? 'dark' : 'light',
                },
                labels: ['Storage used'],
            };
        },
        uploadTrend() {
            return buildUploadTrend(this.recentlyUploaded);
        },
        uploadChartSeries() {
            return [
                {
                    name: 'Datasets',
                    data: this.uploadTrend.values.length
                        ? this.uploadTrend.values
                        : [0, 0, 0, 0],
                },
            ];
        },
        uploadChartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                    fontFamily: 'Omnes Regular, Omnes, Roboto, sans-serif',
                },
                colors: [this.chartThemeColors.primary],
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        columnWidth: '46%',
                    },
                },
                dataLabels: { enabled: false },
                grid: {
                    borderColor: this.chartThemeColors.grid,
                    strokeDashArray: 4,
                },
                tooltip: {
                    theme: this.darkTheme ? 'dark' : 'light',
                },
                xaxis: {
                    categories: this.uploadTrend.categories.length
                        ? this.uploadTrend.categories
                        : ['Recent', 'Recent', 'Recent', 'Recent'],
                    labels: {
                        style: { colors: this.chartThemeColors.muted },
                    },
                },
                yaxis: {
                    labels: {
                        style: { colors: [this.chartThemeColors.muted] },
                    },
                },
            };
        },
    },
};
</script>

<style>
.workspace-dashboard {
    color: var(--sd-text);
    display: grid;
    gap: clamp(16px, 1.8vw, 24px);
    max-width: 1680px;
    margin: 0 auto;
    padding: clamp(16px, 1.8vw, 28px);
}

.workspace-dashboard__no-access {
    display: grid;
    min-height: 60vh;
    align-items: center;
}

.workspace-dashboard__no-access-card {
    max-width: 760px;
    padding: clamp(28px, 3vw, 40px);
    border: 1px solid var(--sd-border);
    border-radius: 28px;
    background:
        radial-gradient(circle at top left, rgba(51, 142, 202, 0.12), transparent 32%),
        var(--sd-surface);
    box-shadow: var(--sd-shadow-soft);
}

.workspace-dashboard__no-access-card h1 {
    margin: 10px 0 12px;
    color: var(--sd-text);
    font-size: clamp(2rem, 3vw, 3rem);
    line-height: 1.05;
}

.workspace-dashboard__no-access-card p {
    margin: 0;
    color: var(--sd-text-secondary);
    font-size: 1rem;
}

.workspace-dashboard__hero {
    display: grid;
    grid-template-columns: minmax(0, 1.48fr) minmax(300px, 0.92fr);
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--sd-border);
    border-radius: 28px;
    background:
        linear-gradient(135deg, var(--sd-surface) 0%, var(--sd-surface-soft) 100%);
    box-shadow: var(--sd-shadow-soft);
}

.workspace-dashboard__hero-copy {
    display: grid;
    align-content: start;
    gap: 12px;
    padding: clamp(20px, 2vw, 28px);
}

.workspace-dashboard__eyebrow,
.workspace-dashboard__section-kicker,
.workspace-dashboard__pill-label,
.workspace-dashboard__metric-content span,
.workspace-dashboard__subscription-grid span,
.workspace-dashboard__team-summary span {
    color: var(--sd-text-secondary);
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.workspace-dashboard__hero h1,
.workspace-dashboard__section-head h2 {
    margin: 0;
    color: var(--sd-text);
    font-size: clamp(1.7rem, 2.6vw, 2.5rem);
    line-height: 1.05;
}

.workspace-dashboard__hero p,
.workspace-dashboard__dataset-copy p,
.workspace-dashboard__feed-item p,
.workspace-dashboard__action-tile span,
.workspace-dashboard__empty-state {
    color: var(--sd-text-secondary);
}

.workspace-dashboard__separator {
    margin: 0 6px;
    opacity: 0.55;
}

.workspace-dashboard__hero-meta,
.workspace-dashboard__hero-pills,
.workspace-dashboard__hero-actions,
.workspace-dashboard__hero-glance,
.workspace-dashboard__metrics,
.workspace-dashboard__primary-grid,
.workspace-dashboard__content-grid,
.workspace-dashboard__insights-grid,
.workspace-dashboard__workspace-grid,
.workspace-dashboard__signal-grid,
.workspace-dashboard__analytics-grid,
.workspace-dashboard__quota-list,
.workspace-dashboard__action-grid,
.workspace-dashboard__team-summary,
.workspace-dashboard__subscription-grid {
    display: grid;
    gap: 16px;
}

.workspace-dashboard__hero-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 2px;
}

.workspace-dashboard__hero-meta span {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 6px 12px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 999px;
    background: var(--sd-surface-soft);
    color: var(--sd-text-secondary);
    font-size: 12px;
    font-weight: 600;
}

.workspace-dashboard__hero-pills {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 6px 0 0;
}

.workspace-dashboard__pill,
.workspace-dashboard__glance-card {
    padding: 14px 16px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 18px;
    background: var(--sd-surface-soft);
}

.workspace-dashboard__pill strong,
.workspace-dashboard__glance-card strong,
.workspace-dashboard__metric-content strong,
.workspace-dashboard__dataset-copy h3,
.workspace-dashboard__feed-item strong,
.workspace-dashboard__team-item h3,
.workspace-dashboard__subscription-grid strong,
.workspace-dashboard__team-summary strong,
.workspace-dashboard__action-tile strong {
    display: block;
    color: var(--sd-text);
}

.workspace-dashboard__pill--accent {
    border-color: var(--sd-primary-border-muted);
    background: var(--sd-primary-surface-soft-alpha);
}

.workspace-dashboard__hero-actions {
    grid-template-columns: repeat(3, max-content);
    align-items: center;
    margin-top: 4px;
}

.workspace-dashboard__hero-visual {
    position: relative;
    min-height: 240px;
    background-color: var(--sd-surface-soft);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}

.workspace-dashboard__hero-visual-overlay {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: 18px;
    background:
        linear-gradient(180deg, rgba(10, 20, 36, 0.04), rgba(10, 20, 36, 0.45));
}

.workspace-dashboard__hero-chip {
    align-self: flex-end;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(227, 255, 242, 0.92);
    color: #0f8a59;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.workspace-dashboard__hero-glance {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-content: start;
    gap: 12px;
}

.workspace-dashboard__glance-card span,
.workspace-dashboard__metric-content small,
.workspace-dashboard__feed-item small,
.workspace-dashboard__metric-content em {
    color: var(--sd-text-secondary);
    font-style: normal;
}

.workspace-dashboard__metrics {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 0;
}

.workspace-dashboard__metric-card,
.workspace-dashboard__upload-card,
.workspace-dashboard__recent-card,
.workspace-dashboard__subscription-card,
.workspace-dashboard__analytics-card,
.workspace-dashboard__quick-actions-card,
.workspace-dashboard__team-card,
.workspace-dashboard__activity-card,
.workspace-dashboard__notifications-card,
.workspace-dashboard__help-card {
    border: 1px solid var(--sd-border);
    border-radius: 22px;
    background: var(--sd-surface);
    box-shadow: var(--sd-shadow-soft);
}

.workspace-dashboard__metric-card {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 16px 18px;
    min-height: 120px;
    transition:
        transform 0.18s ease,
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.workspace-dashboard__metric-content {
    display: grid;
    gap: 4px;
}

.workspace-dashboard__metric-content strong {
    font-size: 1.45rem;
    line-height: 1.05;
}

.workspace-dashboard__metric-content small {
    font-size: 12px;
}

.workspace-dashboard__metric-content em {
    margin-top: 2px;
    font-size: 12px;
}

.workspace-dashboard__metric-card:hover,
.workspace-dashboard__upload-card:hover,
.workspace-dashboard__recent-card:hover,
.workspace-dashboard__subscription-card:hover,
.workspace-dashboard__analytics-card:hover,
.workspace-dashboard__quick-actions-card:hover,
.workspace-dashboard__team-card:hover,
.workspace-dashboard__activity-card:hover,
.workspace-dashboard__notifications-card:hover {
    border-color: var(--sd-primary-border-muted);
    box-shadow: 0 14px 40px rgba(18, 42, 74, 0.08);
    transform: translateY(-1px);
}

.workspace-dashboard__metric-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: var(--sd-surface-soft);
    color: var(--sd-primary);
}

.workspace-dashboard__metric-icon.tone-success,
.workspace-dashboard__feed-dot.tone-success {
    color: var(--sd-success);
}

.workspace-dashboard__metric-icon.tone-warning,
.workspace-dashboard__feed-dot.tone-warning {
    color: var(--sd-warning);
}

.workspace-dashboard__metric-icon.tone-info,
.workspace-dashboard__feed-dot.tone-info {
    color: var(--sd-primary);
}

.workspace-dashboard__progress {
    width: 100%;
    height: 8px;
    margin: 8px 0 6px;
    border-radius: 999px;
    background: var(--sd-surface-soft);
    overflow: hidden;
}

.workspace-dashboard__progress-bar {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--sd-primary), var(--sd-success));
}

.workspace-dashboard__primary-grid {
    grid-template-columns: minmax(300px, 0.78fr) minmax(0, 1.42fr) minmax(360px, 1.1fr);
}

.workspace-dashboard__content-grid {
    grid-template-columns: minmax(0, 2.2fr) minmax(320px, 0.8fr);
    align-items: start;
}

.workspace-dashboard__main-column,
.workspace-dashboard__sidebar-column {
    display: grid;
    gap: 12px;
    min-width: 0;
}

.workspace-dashboard__sidebar-column {
    align-content: start;
}

.workspace-dashboard__insights-grid {
    grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.85fr);
}

.workspace-dashboard__workspace-grid {
    grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1.1fr);
    align-items: start;
}

.workspace-dashboard__section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
}

.workspace-dashboard__section-head--tight {
    margin-bottom: 14px;
}

.workspace-dashboard__section-head--compact {
    align-items: center;
    margin-bottom: 12px;
}

.workspace-dashboard__section-head h2 {
    font-size: 1.3rem;
    margin-bottom: 0;
}

.workspace-dashboard__section-note {
    color: var(--sd-text-secondary);
    font-size: 13px;
}

.workspace-dashboard__upload-card,
.workspace-dashboard__recent-card,
.workspace-dashboard__subscription-card,
.workspace-dashboard__analytics-card,
.workspace-dashboard__quick-actions-card,
.workspace-dashboard__team-card,
.workspace-dashboard__activity-card,
.workspace-dashboard__notifications-card,
.workspace-dashboard__help-card {
    padding: 20px;
}

.workspace-dashboard__quota-list,
.workspace-dashboard__subscription-grid,
.workspace-dashboard__team-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.workspace-dashboard__quota-list {
    grid-template-columns: 1fr;
    gap: 0;
}

.workspace-dashboard__quota-list > div,
.workspace-dashboard__subscription-grid > div {
    min-width: 0;
}

.workspace-dashboard__quota-list > div {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px solid var(--sd-border-subtle);
}

.workspace-dashboard__quota-list > div:last-child {
    border-bottom: 0;
}

.workspace-dashboard__quota-list strong,
.workspace-dashboard__subscription-grid strong {
    line-height: 1.3;
    overflow-wrap: anywhere;
}

.workspace-dashboard__upload-platforms {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin: 14px 0 16px;
}

.workspace-dashboard__upload-platform {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 6px 12px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 999px;
    background: var(--sd-surface-soft);
    color: var(--sd-text-secondary);
    font-size: 12px;
    font-weight: 600;
}

.workspace-dashboard__dataset-list,
.workspace-dashboard__dataset-grid,
.workspace-dashboard__feed-list,
.workspace-dashboard__team-list {
    display: grid;
    gap: 12px;
}

.workspace-dashboard__dataset-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.workspace-dashboard__dataset-card,
.workspace-dashboard__dataset-upload-tile {
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 14px;
    background: var(--sd-surface);
}

.workspace-dashboard__dataset-card {
    display: grid;
    grid-template-rows: auto 1fr auto;
}

.workspace-dashboard__dataset-media {
    position: relative;
    min-height: 112px;
    overflow: hidden;
    background: var(--sd-surface-soft);
}

.workspace-dashboard__dataset-media .v-img,
.workspace-dashboard__dataset-media .v-img__img {
    min-height: 112px;
}

.workspace-dashboard__dataset-status {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 4px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
}

.workspace-dashboard__dataset-card-body {
    display: grid;
    gap: 10px;
    padding: 14px;
}

.workspace-dashboard__dataset-card-body h3 {
    margin: 0 0 2px;
    color: var(--sd-text);
    font-size: 1rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.workspace-dashboard__dataset-detail-block {
    display: grid;
    gap: 3px;
}

.workspace-dashboard__dataset-detail-block span,
.workspace-dashboard__dataset-footer span,
.workspace-dashboard__dataset-upload-tile > span {
    color: var(--sd-text-secondary);
    font-size: 12px;
}

.workspace-dashboard__dataset-detail-block a,
.workspace-dashboard__dataset-detail-block strong {
    color: var(--sd-primary);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
}

.workspace-dashboard__dataset-detail-block p {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    color: var(--sd-text-secondary);
    font-size: 13px;
    line-height: 1.35;
}

.workspace-dashboard__dataset-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: center;
    gap: 10px;
    padding: 11px 14px 14px;
}

.workspace-dashboard__dataset-footer span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
}

.workspace-dashboard__dataset-upload-tile {
    display: grid;
    align-content: center;
    justify-items: center;
    gap: 10px;
    min-height: 100%;
    padding: 24px;
    border-style: dashed;
    border-color: rgba(60, 100, 244, 0.28);
    background:
        radial-gradient(circle at 50% 28%, rgba(60, 100, 244, 0.08), transparent 34%),
        var(--sd-surface);
    color: inherit;
    text-align: center;
    text-decoration: none;
}

.workspace-dashboard__dataset-upload-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: var(--sd-primary-surface-soft-alpha);
    color: var(--sd-primary);
}

.workspace-dashboard__dataset-upload-tile strong {
    color: var(--sd-text);
    font-size: 1.08rem;
}

.workspace-dashboard__dataset-upload-tile p {
    max-width: 240px;
    margin: 0;
    color: var(--sd-text-secondary);
    font-size: 0.92rem;
    line-height: 1.45;
}

.workspace-dashboard__upload-platforms--compact {
    justify-content: center;
    margin: 0;
}

.workspace-dashboard__upload-platforms--compact span {
    min-height: 24px;
    padding: 3px 8px;
    border: 1px solid var(--sd-chip-border);
    border-radius: 999px;
    background: var(--sd-primary-surface-soft-alpha);
    color: var(--sd-primary);
    font-size: 11px;
    font-weight: 600;
}

.workspace-dashboard__dataset-item,
.workspace-dashboard__team-item,
.workspace-dashboard__feed-item {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 12px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 18px;
    background: var(--sd-surface-soft);
}

.workspace-dashboard__dataset-thumb {
    flex: 0 0 116px;
    overflow: hidden;
    border-radius: 14px;
}

.workspace-dashboard__dataset-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 116px;
    height: 72px;
    background: var(--sd-primary-surface-soft-alpha);
    color: var(--sd-primary);
}

.workspace-dashboard__dataset-copy {
    flex: 1 1 auto;
    min-width: 0;
}

.workspace-dashboard__dataset-copy h3,
.workspace-dashboard__team-item h3 {
    margin: 0 0 6px;
    font-size: 0.98rem;
    overflow-wrap: anywhere;
}

.workspace-dashboard__dataset-meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    color: var(--sd-text-secondary);
    font-size: 13px;
}

.workspace-dashboard__status {
    color: var(--sd-success);
    font-weight: 700;
}

.workspace-dashboard__status--info {
    color: var(--sd-primary);
}

.workspace-dashboard__status--warning {
    color: var(--sd-warning);
}

.workspace-dashboard__status--success {
    color: var(--sd-success);
}

.workspace-dashboard__dataset-actions {
    display: grid;
    gap: 8px;
    justify-items: stretch;
}

.workspace-dashboard__dataset-actions .v-btn {
    min-width: 104px;
}

.workspace-dashboard__ownership-note {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 12px 14px;
    border: 1px solid rgba(60, 100, 244, 0.18);
    border-radius: 10px;
    background: rgba(60, 100, 244, 0.06);
    color: var(--sd-primary);
    font-size: 13px;
    line-height: 1.4;
}

.workspace-dashboard__access-team-list {
    display: grid;
    gap: 12px;
}

.workspace-dashboard__access-team-item {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 10px;
}

.workspace-dashboard__team-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--sd-primary);
    color: var(--sd-text-inverse);
    font-size: 12px;
    font-weight: 700;
}

.workspace-dashboard__access-team-item h3 {
    margin: 0 0 2px;
    color: var(--sd-text);
    font-size: 0.92rem;
    line-height: 1.25;
}

.workspace-dashboard__access-team-item p {
    margin: 0;
    color: var(--sd-text-secondary);
    font-size: 12px;
}

.workspace-dashboard__analytics-grid,
.workspace-dashboard__action-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.workspace-dashboard__analytics-highlights {
    display: grid;
    gap: 14px;
    margin-bottom: 16px;
}

.workspace-dashboard__analytics-summary {
    padding: 16px 18px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 18px;
    background: var(--sd-surface-soft);
}

.workspace-dashboard__analytics-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 12px;
}

.workspace-dashboard__analytics-summary-grid span {
    display: block;
    color: var(--sd-text-secondary);
    font-size: 12px;
}

.workspace-dashboard__chart-wrap {
    min-width: 0;
    padding: 14px 14px 8px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 18px;
    background: var(--sd-surface-soft);
}

.workspace-dashboard__chart-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 10px;
}

.workspace-dashboard__chart-title strong {
    color: var(--sd-text);
    font-size: 0.98rem;
}

.workspace-dashboard__chart-title span {
    color: var(--sd-text-secondary);
    font-size: 12px;
}

.workspace-dashboard__action-tile {
    display: grid;
    gap: 8px;
    min-height: 112px;
    padding: 16px;
    border: 1px solid var(--sd-border-subtle);
    border-radius: 18px;
    background: var(--sd-surface-soft);
    color: inherit;
    text-decoration: none;
    transition:
        border-color 0.2s ease,
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.workspace-dashboard__action-tile:hover {
    border-color: var(--sd-primary-border-muted);
    transform: translateY(-2px);
    box-shadow: var(--sd-shadow-soft);
}

.workspace-dashboard__action-grid--support {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.workspace-dashboard__signal-stack {
    min-width: 0;
    padding: 20px;
    border: 1px solid var(--sd-border);
    border-radius: 22px;
    background: var(--sd-surface);
    box-shadow: var(--sd-shadow-soft);
}

.workspace-dashboard__signal-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.workspace-dashboard__feed-dot {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: currentColor;
    color: var(--sd-text-secondary);
}

.workspace-dashboard__feed-item small {
    display: block;
    margin: 2px 0 4px;
    color: var(--sd-text-muted);
    font-size: 12px;
}

.workspace-dashboard__sidebar-column .workspace-dashboard__feed-list {
    gap: 14px;
}

.workspace-dashboard__sidebar-column .workspace-dashboard__feed-item {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 10px;
    align-items: start;
    padding: 0;
    border: 0;
    background: transparent;
}

.workspace-dashboard__feed-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--sd-success-surface-alpha);
    color: var(--sd-success);
}

.workspace-dashboard__feed-icon.tone-info {
    background: var(--sd-primary-surface-soft-alpha);
    color: var(--sd-primary);
}

.workspace-dashboard__feed-icon.tone-warning {
    background: rgba(243, 165, 95, 0.14);
    color: var(--sd-warning);
}

.workspace-dashboard__sidebar-column .workspace-dashboard__feed-item strong {
    font-size: 0.84rem;
    line-height: 1.25;
}

.workspace-dashboard__sidebar-column .workspace-dashboard__feed-item p {
    margin: 2px 0 0;
    font-size: 12px;
    line-height: 1.35;
}

.workspace-dashboard__sidebar-column .workspace-dashboard__feed-item small {
    margin: 1px 0 0;
    white-space: nowrap;
}

.workspace-dashboard__help {
    padding-bottom: 4px;
}

@media (max-width: 1260px) {
    .workspace-dashboard__hero {
        grid-template-columns: minmax(0, 1fr) minmax(280px, 0.82fr);
    }

    .workspace-dashboard__primary-grid,
    .workspace-dashboard__content-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workspace-dashboard__primary-grid > :last-child {
        grid-column: 1 / -1;
    }

    .workspace-dashboard__insights-grid,
    .workspace-dashboard__workspace-grid {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__dataset-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workspace-dashboard__metrics {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 1080px) {
    .workspace-dashboard__hero,
    .workspace-dashboard__primary-grid,
    .workspace-dashboard__content-grid {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__hero-visual {
        min-height: 220px;
    }
}

@media (max-width: 900px) {
    .workspace-dashboard {
        padding: 16px;
    }

    .workspace-dashboard__hero-actions,
    .workspace-dashboard__hero-pills,
    .workspace-dashboard__hero-meta,
    .workspace-dashboard__hero-glance,
    .workspace-dashboard__quota-list,
    .workspace-dashboard__subscription-grid,
    .workspace-dashboard__team-summary,
    .workspace-dashboard__analytics-grid,
    .workspace-dashboard__analytics-summary-grid,
    .workspace-dashboard__action-grid,
    .workspace-dashboard__action-grid--support {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__signal-grid {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__metrics {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__hero-copy {
        padding: 22px;
    }

    .workspace-dashboard__dataset-item {
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .workspace-dashboard__dataset-grid {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__dataset-actions {
        width: 100%;
        grid-template-columns: 1fr 1fr;
    }

    .workspace-dashboard__hero-visual {
        min-height: 220px;
    }
}
</style>
